<template>
  <UiComponentWrapper class="template-virtual-background">
    <UiComponentWrapper class="jumbotron">
      <OrganismHero
        :primary-button="{
          ...props.primaryCallToAction,
          label: 'Header section CTA',
        }"
        :secondary-button="{
          ...props.secondaryCallToAction,
          label: 'Header section CTA',
        }"
        @secondary:click="emit('heroSecondary:click', $event)"
      >
        <template #heading>
          <slot name="title" />
        </template>
        <template #paragraph>
          <slot name="description" />
        </template>
      </OrganismHero>
      <UiComponentWrapper class="backgrounds">
        <UiComponentWrapper class="background one">
          <img
            :alt="props.image1.alt"
            :height="image1Height"
            :src="`${props.image1.url}?fm=png&auto=format`"
            :srcset="`${props.image1.url}?h=${image1Height}&w=${image1Width}&fit=crop&fm=png&auto=format&q=80&dpr=1 1x, ${props.image1.url}?h=${image1Height}&w=${image1Width}&fit=crop&fm=png&auto=format&q=40&dpr=2 2x, ${props.image1.url}?h=${image1Height}&w=${image1Width}&fit=crop&fm=png&auto=format&q=20&dpr=3 3x`"
            :width="image1Width"
          >
        </UiComponentWrapper>
        <UiComponentWrapper class="background two">
          <img
            :alt="props.image2.alt"
            :height="image2Height"
            :src="`${props.image2.url}?fm=png&auto=format`"
            :srcset="`${props.image2.url}?h=${image2Height}&w=${image2Width}&fit=crop&fm=png&auto=format&q=80&dpr=1 1x, ${props.image2.url}?h=${image2Height}&w=${image2Width}&fit=crop&fm=png&auto=format&q=40&dpr=2 2x, ${props.image2.url}?h=${image2Height}&w=${image2Width}&fit=crop&fm=png&auto=format&q=20&dpr=3 3x`"
            :width="image2Width"
          >
        </UiComponentWrapper>
        <UiComponentWrapper class="background three">
          <img
            :alt="props.image3.alt"
            :height="image3Height"
            :src="`${props.image3.url}?fm=png&auto=format`"
            :srcset="`${props.image3.url}?h=${image3Height}&w=${image3Width}&fit=crop&fm=png&auto=format&q=80&dpr=1 1x, ${props.image3.url}?h=${image3Height}&w=${image3Width}&fit=crop&fm=png&auto=format&q=40&dpr=2 2x, ${props.image3.url}?h=${image3Height}&w=${image3Width}&fit=crop&fm=png&auto=format&q=20&dpr=3 3x`"
            :width="image3Width"
          >
        </UiComponentWrapper>
        <UiComponentWrapper class="animation">
          <UiComponentWrapper
            class="inner"
            @mousemove="onMouseMove"
          >
            <img
              v-for="(image, index) in props.animatedImages"
              :key="`frame-${index}-${animationImageWidth}x${animationImageHeight}`"
              :alt="image.alt"
              :height="animationImageHeight"
              :class="{ active: index <= currentImageIndex }"
              :src="`${image.url}?h=${animationImageHeight}&w=${animationImageWidth}&fit=crop&fm=png&auto=format`"
              :srcset="`${image.url}?h=${animationImageHeight}&w=${animationImageWidth}&fit=crop&fm=png&auto=format&q=80&dpr=1 1x, ${image.url}?h=${animationImageHeight}&w=${animationImageWidth}&fit=crop&fm=png&auto=format&q=40&dpr=2 2x, ${image.url}?h=${animationImageHeight}&w=${animationImageWidth}&fit=crop&fm=png&auto=format&q=20&dpr=3 3x`"
              :width="animationImageWidth"
            >
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentWrapper>
    <MoleculeSearchBlock
      :key="searchedTerm"
      :content="searchedTerm"
      @search:reset="onReset"
      @search:click="onSubmit"
    >
      <template #title>
        <slot name="subtitle" />
      </template>
      <template #placeholder>
        <slot name="placeholder" />
      </template>
    </MoleculeSearchBlock>
    <UiComponentWrapper
      v-if="props.categories.length > 0"
      class="categories"
    >
      <NuxtLink
        class="all"
        :to="
          localePath({
            name: 'virtual-background-library',
          })
        "
      >
        <slot name="all" />
      </NuxtLink>
      <NuxtLink
        v-for="{ id, active, name, slug } in props.categories"
        :key="id"
        class="tag"
        :class="{ active }"
        :to="
          active
            ? localePath({
              name: 'virtual-background-library',
            })
            : localePath({
              name: 'virtual-background-library-category-slug',
              params: {
                slug,
              },
            })
        "
      >
        {{ name }}
      </NuxtLink>
    </UiComponentWrapper>

    <UiComponentWrapper
      v-if="paginatedCards.length > 0"
      class="cards"
    >
      <template
        v-for="({
          id,
          category,
          image,
          isDaylightBackground,
          pattern,
          primaryCallToAction: primaryCTA,
          secondaryCallToAction: secondaryCTA,
          slug,
          text,
          title,
          type,
        }, index) in paginatedCards"
        :key="`card-${type}-${id || index}`"
      >
        <div
          v-if="type === 'push-card'"
          class="empty"
        />
        <OrganismCallToAction
          v-if="type === 'push-card'"
          :image-desktop="pattern"
          :image-mobile="pattern"
          :primary-button="{
            ...primaryCTA,
            label: 'Push grid section CTA',
            slot: primaryCTA.alternativeSlot || primaryCTA.slot,
          }"
          :secondary-button="{
            ...secondaryCTA,
            label: 'Push grid section CTA',
          }"
        >
          <template #heading>
            {{ title }}
          </template>
          <template #paragraph>
            {{ text }}
          </template>
        </OrganismCallToAction>
        <MoleculeVirtualBackgroundCard
          v-if="type === 'background-card'"
          :image="image"
          :is-daylight="isDaylightBackground"
          :to="
            localePath({
              name: 'virtual-background-library-slug',
              params: {
                slug,
              },
            })
          "
        >
          <template #tag>
            {{ category.categoryShortName }}
          </template>
          <template #paragraph>
            {{ title }}
          </template>
        </MoleculeVirtualBackgroundCard>
      </template>
    </UiComponentWrapper>
    <UiComponentWrapper
      v-else
      class="no-match"
    >
      <UiComponentWrapper class="body">
        <UiComponentImage
          v-if="props.noMatchImage && props.noMatchImage.url"
          :alt="props.noMatchImage.alt"
          class="image"
          :expected-size="211"
          :height="props.noMatchImage.height"
          :source="props.noMatchImage.url"
          :width="props.noMatchImage.width"
        />
        <UiComponentParagraph>
          <slot name="no-match-title" /><span>
            {{ searchedTerm }}</span>
        </UiComponentParagraph>
        <UiComponentParagraph size="s">
          <slot name="no-match-description" />
        </UiComponentParagraph>
      </UiComponentWrapper>
    </UiComponentWrapper>
    <MoleculePagination
      v-if="numberOfPages > 1"
      :count="numberOfPages"
      :root="props.paginationRoot"
      :value="props.currentPage"
    />
  </UiComponentWrapper>
</template>

<script setup>
import { gsap } from 'gsap'

// Emitter
const emit = defineEmits([
  'heroSecondary:click',
])

// Props
const props = defineProps({
  animatedImages: {
    default() {
      return []
    },
    required: false,
    type: Array,
  },
  cards: {
    type: Array,
    required: true,
  },
  categories: {
    default() {
      return []
    },
    required: false,
    type: Array,
  },
  currentPage: {
    required: true,
    type: Number,
  },
  image1: {
    required: true,
    type: Object,
  },
  image2: {
    required: true,
    type: Object,
  },
  image3: {
    required: true,
    type: Object,
  },
  noMatchImage: {
    required: true,
    type: Object,
  },
  primaryCallToAction: {
    required: true,
    type: Object,
  },
  paginationRoot: {
    type: String,
    required: true,
  },
  pushCard: {
    default() {
      return {}
    },
    required: false,
    type: Object,
  },
  secondaryCallToAction: {
    required: true,
    type: Object,
  },
})

// Composables
const { track } = useAnalyticsJS()
const { locale } = useI18n()
const localePath = useLocalePath()
const getRouteBaseName = useRouteBaseName()
const { push } = useRouter()
const { width: screenWidth } = useScreenSize()

// States
const searchedTerm = useState('virtual-background-library-searched-term', () => '')

// Ref
const currentImageIndex = ref(0)
const hasHiddenCategories = ref(false)
const numberOfVirtualBackgroundsPerPage = ref(36)
const showMoreCategories = ref(false)

// Variables
let timeline = null

// Computed Properties
const animationImageHeight = computed(() => {
  return screenWidth.value >= 768 ? 256 : 122
})

const animationImageWidth = computed(() => {
  return screenWidth.value >= 768 ? 456 : 218
})

const cardsWithPush = computed(() => {
  const cards = [...filteredCards.value]
  if (hasPushCard.value)
    cards.splice(screenWidth.value >= 1328 ? 6 : 2, 0, props.pushCard)
  return cards
})

const filteredCards = computed(() => {
  return props.cards.filter(({ title, searchLexicalField }) =>
    searchedTerm.value.trim() === ''
      ? true
      : [title, ...searchLexicalField.split(',')].some(term =>
          term
            .trim()
            .toLowerCase()
            .includes(searchedTerm.value.trim().toLowerCase()),
        ),
  )
})

const hasPushCard = computed(() => {
  return filteredCards.value.length >= 6
})

const image1Height = computed(() => {
  return screenWidth.value >= 768 ? 113.3 : 54
})

const image1Width = computed(() => {
  return screenWidth.value >= 768 ? 195 : 93
})

const image2Height = computed(() => {
  return screenWidth.value >= 768 ? 139 : 66
})

const image2Width = computed(() => {
  return screenWidth.value >= 768 ? 239 : 114
})

const image3Height = computed(() => {
  return screenWidth.value >= 768 ? 93 : 44
})

const image3Width = computed(() => {
  return screenWidth.value >= 768 ? 160 : 76
})

const numberOfPages = computed(() => {
  return hasPushCard.value
    ? Math.ceil(
        (cardsWithPush.value.length + 1)
        / numberOfVirtualBackgroundsPerPage.value,
      )
    : 1
})

const paginatedCards = computed(() => {
  if (props.currentPage === 1) {
    return cardsWithPush.value.slice(
      0,
      numberOfVirtualBackgroundsPerPage.value - 1,
    )
  }
  else {
    return cardsWithPush.value.slice(
      numberOfVirtualBackgroundsPerPage.value
      - 1
      + (props.currentPage - 2) * numberOfVirtualBackgroundsPerPage.value,
      numberOfVirtualBackgroundsPerPage.value
      - 1
      + (props.currentPage - 1) * numberOfVirtualBackgroundsPerPage.value,
    )
  }
})

const tagSize = computed(() => {
  return screenWidth.value >= 768 ? 'm' : 's'
})

// Methods
function createTimeline() {
  timeline = gsap.timeline()
  timeline.from(
    document.querySelector('.page .jumbotron .backgrounds .animation'),
    {
      rotation: 0,
      y: -13,
      duration: 2,
      ease: 'expo.inOut',
    },
    0.4,
  )
  timeline.from(
    document.querySelector('.page .jumbotron .backgrounds .background.one'),
    {
      rotation: 0,
      left: '50%',
      top: '50%',
      x: '-50%',
      y: '-50%',
      duration: 2,
      ease: 'expo.inOut',
    },
    0.4,
  )
  timeline.from(
    document.querySelector('.page .jumbotron .backgrounds .background.two'),
    {
      rotation: 0,
      right: '50%',
      top: '50%',
      x: '50%',
      y: '-50%',
      duration: 2,
      ease: 'expo.inOut',
    },
    0.4,
  )
  timeline.from(
    document.querySelector('.page .jumbotron .backgrounds .background.three'),
    {
      rotation: 0,
      left: '50%',
      top: '50%',
      x: '-50%',
      y: '-50%',
      duration: 2,
      ease: 'expo.inOut',
    },
    0.4,
  )
  timeline.play()
}

function onMouseMove({ offsetX }) {
  const { offsetWidth } = document.querySelector(
    '.page .jumbotron .backgrounds .animation',
  )
  currentImageIndex.value = Math.abs(
    Math.floor((offsetX / offsetWidth) * props.animatedImages.length),
  )
}

async function onReset() {
  await setSearchedTerm('')
}

async function onSubmit(searchedTerm) {
  await setSearchedTerm(searchedTerm)

  track('Term searched', {
    category: 'Search',
    label: 'Virtual background search',
    searched_term: searchedTerm,
    locale: locale.value,
  })

  if (getRouteBaseName() !== 'virtual-background-library') {
    push({
      path: localePath('virtual-background-library'),
    })
  }
}

function setSearchedTerm(term) {
  searchedTerm.value = term
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  timeline?.kill()
  timeline = null
})

onMounted(() => {
  createTimeline()
})
</script>

<style lang="postcss" scoped>
.template-virtual-background {
  @apply flex-col;

  .jumbotron {
    @apply flex-col items-center mt-16 mb-6;

    @screen lg {
      @apply flex-row flex-wrap justify-between mt-0 mb-[37px];
    }

    :deep(.hero) {
      @screen lg {
        @apply items-start w-span6;
      }

      .h1,
      .description {
        @screen lg {
          @apply text-left;
        }
      }

      .button-list {
        @screen lg {
          @apply justify-start;
        }
      }
    }

    :deep(.backgrounds) {
      @apply relative h-[193px] mt-6;

      @screen md {
        @apply h-[405px];
      }

      @screen lg {
        @apply w-span6 h-[504px] mt-0;
      }

      .background {
        @apply absolute overflow-hidden transform;

        &.one {
          @apply left-0 top-[11.44px] h-[54px] w-[93px] rounded-[4.9px] shadow-light2 rotate-[-9deg];

          @screen md {
            @apply top-[24.01px] h-[113.3px] w-[195px] rounded-[10.3px];
          }

          @screen lg {
            @apply left-[33.93px] top-[50.84px];
          }
        }

        &.two {
          @apply right-0 top-[2.86px] h-[66.11px] w-[113.76px] rounded-[5.9px] shadow-light1 rotate-[7deg];

          @screen md {
            @apply top-[6.01px] h-[138.8px] w-[238.8px] rounded-[12.6px];
          }

          @screen lg {
            @apply right-[29.07px] top-[26px];
          }
        }

        &.three {
          @apply right-[106.86px] top-[134.34px] h-[44.3px] w-[76.3px] rounded-[4.1px] shadow-light3 rotate-[-5deg];

          @screen md {
            @apply right-[224.31px] top-[282.01px] h-[93.1px] w-[160.2px] rounded-[8.4px];
          }

          @screen lg {
            @apply right-[137.81px] top-[397.04px];
          }
        }
      }

      .animation {
        @apply absolute left-1/2 top-[25.84px] w-[218px] h-[122px] bg-white rounded-[11.4px] shadow-light5 overflow-hidden transform -translate-x-1/2 rotate-[3deg];

        @screen md {
          @apply w-[456px] h-64 rounded-3xl shadow-light5 overflow-hidden;
        }

        @screen lg {
          @apply top-[145.2px] translate-y-0;
        }

        .inner {
          @apply relative w-full h-full;

          img {
            @apply absolute left-0 top-0 w-full h-full opacity-0 invisible transition-all duration-300 ease-in-out;

            &:not(:first-child) {
              @apply hidden;

              @screen lg {
                @apply flex;
              }
            }

            &.active {
              @apply opacity-100 visible;
            }
          }
        }
      }
    }
  }

  .categories {
    @apply flex-wrap justify-center gap-4 mt-8;

    @screen xl {
      @apply justify-start mt-12;
    }

    .all {
      @apply flex w-auto font-medium text-sm text-livestorm-blue-700 leading-6 cursor-pointer;
    }

    .tag {
      @apply flex items-center h-6 w-auto px-2 bg-livestorm-blue-100 font-medium text-xs text-livestorm-blue-700 leading-4 rounded-xs cursor-pointer transition-colors duration-300 ease-in-out;

      &.active {
        @apply bg-livestorm-blue-700 text-white;
      }
    }
  }

  .cards {
    @apply mt-20 flex-wrap;

    .empty {
      @apply w-0;
    }

    .virtual-background-card,
    .call-to-action {
      @apply mt-8 w-full;

      @screen md {
        @apply mt-10;
        width: calc(50% - 20px);
      }

      @screen xl {
        width: 290px;
      }

      &:first-child {
        @apply mt-0;
      }

      &:nth-child(2) {
        @screen md {
          @apply mt-0;
        }
      }

      &:nth-child(3) {
        @screen xl {
          @apply mt-0;
        }
      }

      &:nth-child(4) {
        @screen xl {
          @apply mt-0;
        }
      }

      &:nth-child(2n) {
        @screen md {
          @apply ml-10;
        }
      }

      &:nth-child(4n + 3) {
        @screen xl {
          @apply ml-10;
        }
      }
    }

    .call-to-action {
      @apply p-6;

      @screen md {
        @apply ml-0 p-10 w-full;
        margin-left: 0 !important;
      }

      @screen xl {
        @apply py-0;
        width: 620px;
        margin-left: 40px !important;
      }

      :deep(.h2) {
        @apply mt-0 text-2xl leading-8 !important;
      }

      :deep(p) {
        @apply text-grey-blue-100 !important;
      }

      :deep(.buttons) {
        @apply mt-4;

        .button {
          @apply w-full;

          @screen md {
            @apply w-auto;
          }

          &.primary {
            @apply bg-livestorm-blue-700 !important;

            &:hover {
              @apply bg-livestorm-blue-800 !important;
            }
          }
        }
      }
    }
  }

  .no-match {
    @apply flex-row justify-center mt-20;

    .body {
      @apply flex-col items-center w-full;
      max-width: 343px;

      .image {
        @apply rounded-none;
      }

      p {
        @apply text-winter-green-900 text-center;

        span {
          @apply inline text-livestorm-blue-700 font-medium whitespace-nowrap;
        }
      }

      p + p {
        @apply mt-4 text-grey-blue-600;
      }
    }
  }

  .pagination {
    @apply mt-12;

    @screen md {
      @apply mt-20;
    }
  }
}
</style>
